import { Box, Button, ConfirmationModal, Select, Text, Textarea, Toggle } from '@tocktix/pantry';
import {
  BulkUserAction,
  DeactivationReason,
} from 'tools/bff/generated/user/user_administration_pb';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import { DeactivatableUser } from 'tools/users/BulkUserPreviewTable';
import { REASON_OPTIONS } from 'tools/users/deactivationReasons';
import { useActions } from 'utils/connect';
import { userClient } from 'tools/bff/client';

interface BulkUserDeactivateActionProps {
  // Users the batch will act on, resolved and trimmed upstream in the preview step.
  deactivatable: DeactivatableUser[];
  // Fired once a run is accepted so the route can advance to the progress step.
  onStarted: (attachedToExistingRun: boolean) => void;
  onBack: () => void;
}

// Deactivation-specific parameters and submit flow. Each bulk action owns a component like this so
// its fields, validation, confirmation copy, and submit call stay independent of other actions.
const BulkUserDeactivateAction: FunctionComponent<BulkUserDeactivateActionProps> = ({
  deactivatable,
  onStarted,
  onBack,
}) => {
  const { actions } = useActions();
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const canSubmit = reasonCode !== '' && deactivatable.length > 0 && !starting;

  const handleStart = async () => {
    setConfirmOpen(false);
    setStarting(true);
    setStartError(null);
    try {
      const res = await userClient.startBulkUserAction({
        action: BulkUserAction.DEACTIVATE,
        uuids: deactivatable.map((user) => user.gdusUuid),
        // Free-text note -> Resy deactivation_description / GDUS lockout reason.
        reason: note.trim(),
        reasonCode: Number(reasonCode) as DeactivationReason,
        ...(notify ? { sendNotification: true } : {}),
      });
      onStarted(res.attachedToExistingRun);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : String(reason);
      setStartError('Error starting bulk action: ' + message);
      actions.toast.showError('Error starting bulk deactivation');
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={300} style={{ maxWidth: '480px' }}>
        <Select
          id="bulk-user-reason"
          label="Reason"
          placeholder="Select a reason"
          value={reasonCode}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => setReasonCode(event.target.value)}
          required
          fullWidth
          data-testid="bulk-user-reason-select"
        >
          {REASON_OPTIONS.map(({ value, label }) => (
            <Select.Option key={value} value={String(value)}>
              {label}
            </Select.Option>
          ))}
        </Select>
        <Textarea
          id="bulk-user-note"
          label="Note (optional)"
          hint="Free-text note saved on each account lockout and the Resy deactivation record."
          value={note}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setNote(event.target.value)}
          fullWidth
          data-testid="bulk-user-note-input"
        />
        <Toggle
          id="bulk-user-notify"
          label="Notify users"
          checked={notify}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setNotify(event.target.checked)}
          data-testid="bulk-user-notify-toggle"
        />
      </Box>
      {startError && (
        <Text variant="body" color="fgDangerBase" data-testid="bulk-user-start-error">
          {startError}
        </Text>
      )}
      <Box display="flex" gap={200}>
        <Button
          variant="outlined"
          label="Back"
          onClick={onBack}
          data-testid="bulk-user-back-button"
        />
        <Button
          color="danger"
          label={starting ? 'Starting...' : `Deactivate ${deactivatable.length} user(s)`}
          onClick={() => setConfirmOpen(true)}
          disabled={!canSubmit}
          data-testid="bulk-user-submit-button"
        />
      </Box>
      {confirmOpen && (
        <ConfirmationModal
          id="bulk-user-confirm"
          variant="danger"
          title="Deactivate users?"
          message={`This will immediately deactivate ${deactivatable.length} account(s). Only one bulk action can run per operator at a time.`}
          labels={{ confirm: 'Deactivate', cancel: 'Cancel' }}
          open
          onConfirm={handleStart}
          onCancel={() => setConfirmOpen(false)}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
};

export default BulkUserDeactivateAction;
